/** Academic levels offered by the platform, in display order. */
export const STUDY_YEARS: readonly string[] = [
  '1ère année', '2ème année', '3ème année', '4ème année',
  '5ème année', '6ème année', '7ème année', 'Résidanat',
];

export type MediaFileType = 'image' | 'video' | 'audio' | 'document';
export type MediaBucket = 'images' | 'videos' | 'audio' | 'documents';

const BUCKET_BY_TYPE: Record<MediaFileType, MediaBucket> = {
  image: 'images',
  video: 'videos',
  audio: 'audio',
  document: 'documents',
};

/** Classifies an uploaded file by its MIME family and returns the storage bucket it belongs to. */
export function mediaKindForFile(file: Pick<File, 'type'>): { fileType: MediaFileType; bucket: MediaBucket } {
  const family = file.type.split('/')[0];
  const fileType: MediaFileType = family === 'image' || family === 'video' || family === 'audio' ? family : 'document';
  return { fileType, bucket: BUCKET_BY_TYPE[fileType] };
}

export interface ProfileNameFields {
  full_name?: string | null;
  name?: string | null;
  fam_name?: string | null;
}

/** Profiles store either `full_name` or the `name` / `fam_name` pair; prefer the former, then the pair. */
export function profileDisplayName(profile: ProfileNameFields | null | undefined, fallback = ''): string {
  if (!profile) return fallback;
  return profile.full_name || [profile.name, profile.fam_name].filter(Boolean).join(' ') || fallback;
}

/** Up to two uppercase initials for an avatar fallback. */
export function initialsFor(label: string, fallback = '?'): string {
  const initials = label.split(/[\s@._-]+/).filter(Boolean).map(part => part[0]).join('').toUpperCase().slice(0, 2);
  return initials || fallback;
}

/** A stable avatar colour class (`tone-0` … `tone-5`) for a name, so the same person always gets the same colour. */
export function avatarTone(label: string | null | undefined): string {
  let hash = 0;
  for (const char of label ?? '') hash = (hash * 31 + char.charCodeAt(0)) | 0;
  return `tone-${Math.abs(hash) % 6}`;
}

export type AdminRole = 'superadmin' | 'admin' | 'contributor' | 'student';

/** French labels for account roles, in the order they are offered in role pickers. */
export const ROLE_LABELS: Readonly<Record<AdminRole, string>> = {
  superadmin: 'Super administrateur',
  admin: 'Administrateur',
  contributor: 'Contributeur',
  student: 'Étudiant',
};

export function roleLabel(role: string | null | undefined): string {
  return ROLE_LABELS[(role || 'student') as AdminRole] ?? ROLE_LABELS.student;
}

/** Only a super administrator may touch admin or super-admin accounts. */
export function canManageAccount(currentRole: string | null | undefined, targetRole: string | null | undefined): boolean {
  if (currentRole === 'superadmin') return true;
  const target = targetRole || 'student';
  return target !== 'superadmin' && target !== 'admin';
}

/**
 * Makes free text safe inside a PostgREST `or=(...)` / `ilike` filter: commas, parentheses and
 * wildcards would otherwise change the meaning of the filter.
 */
export function postgrestSearchTerm(term: string): string {
  return term.trim().replace(/[,()*%\\:"]/g, ' ').replace(/\s+/g, ' ').trim();
}
